import { professorRequest } from "services/requests"
import { getSelectedGroup as getParentSelectedGroup } from "services/parentBridge"
import mockSelectedGroup from "./mocks/selectedGroup"
import { logger } from "utils/logger"
import { isRunningFromIframe } from "common/functions"

const prefix = "groups"

// TODO: replace with professorRequest once the API endpoint is available
export const getSelectedGroup = async (mocked = false) => {
  if (!isRunningFromIframe()) {
    return mocked ? mockSelectedGroup : null
  }
  return await getParentSelectedGroup()
}

export const getCreateLanguages = async ({ groupId = 0 } = {}) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/content/languages?groupId=${groupId}`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error("[groups] Failed to fetch languages:", response.message)
    return []
  }
  const languages =
    response.data?.data?.languages ||
    response.data?.languages ||
    response.data ||
    []
  return languages
}

export const getCreateGrades = async ({
  learningLanguageId = 0,
  groupId = 0,
} = {}) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/content/grades?learningLanguageId=${learningLanguageId}&groupId=${groupId}`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error("[groups] Failed to fetch grades:", response.message)
    return []
  }
  const grades =
    response.data?.data?.grades || response.data?.grades || response.data || []
  return grades
}

export const getCreatePrograms = async ({
  learningLanguageId = 0,
  groupId = 0,
} = {}) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/content/programs?learningLanguageId=${learningLanguageId}&groupId=${groupId}`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error("[groups] Failed to fetch programs:", response.message)
    return []
  }
  const programs =
    response.data?.data?.programs ||
    response.data?.programs ||
    response.data ||
    []
  return programs
}

export const getCreateCurriculums = async ({
  programId = 0,
  learningLanguageId = 0,
  groupId = 0,
} = {}) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/content/curriculums?programId=${programId}&learningLanguageId=${learningLanguageId}&groupId=${groupId}`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error("[groups] Failed to fetch curriculums:", response.message)
    return []
  }
  const curriculums =
    response.data?.data?.curriculums ||
    response.data?.data?.curricula ||
    response.data?.curriculums ||
    response.data?.curricula ||
    response.data ||
    []
  return curriculums
}

export const createGroups = async (payload) => {
  const response = await professorRequest.sendRequest({
    endpoint: prefix,
    body: payload,
    method: "post",
  })
  if (!response.success) {
    logger.error("[groups] Failed to create groups:", response.message)
  }
  return response
}

export const createBatchGroups = async (payload) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/batch`,
    body: payload,
    method: "post",
  })
  if (!response.success) {
    logger.error("[groups] Failed to create batch groups:", response.message)
  }
  return response
}

export const getPedagogicalStatusSummary = async ({ status = "Active" }) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/pedagogicalStatusSummary?status=${status}`,
    body: null,
    method: "get",
  })
  return response
}

export const getGroupTotals = async () => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/totals`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error("[groups] Failed to fetch group totals:", response.message)
    return { totalClasses: 0, totalStudents: 0 }
  }
  const totals = response.data?.data || response.data || {}
  return {
    totalClasses: totals.totalGroups ?? totals.TotalGroups ?? 0,
    totalStudents: totals.totalStudents ?? totals.TotalStudents ?? 0,
  }
}

export const getProfessorGroups = async ({
  status = "Active",
  pageNumber = 1,
  pageSize,
  sortBy = "Name",
  sortDir = "Asc",
  loadDetails = true,
  pedagogicalStatus,
}) => {
  const params = new URLSearchParams({
    status,
    pageNumber: String(pageNumber),
    sortBy,
    sortDir,
  })
  if (typeof pageSize !== "undefined") {
    params.append("pageSize", String(pageSize))
  }
  if (typeof loadDetails !== "undefined") {
    params.append("loadDetails", String(loadDetails))
  }
  if (typeof pedagogicalStatus !== "undefined") {
    params.append("pedagogicalStatus", pedagogicalStatus)
  }
  const queryParams = params.toString()

  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}?${queryParams}`,
    body: null,
    method: "get",
  })

  return response
}

export const updateGroup = async (groupId, payload) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/${groupId}`,
    body: payload,
    method: "put",
  })
  if (!response.success) {
    logger.error("[groups] Failed to update group:", response.message)
  }
  return response
}

export const updateGroupsSequence = async (sequenceGroups) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/sequences`,
    body: sequenceGroups,
    method: "put",
  })
  if (!response.success) {
    logger.error("[groups] Failed to update groups sequence:", response.message)
  }
  return response
}

export const deleteGroup = async ({ groupId, isDeleted = true }) => {
  const endpoint = `${prefix}/${groupId}/deleted?isDeleted=${isDeleted}`
  logger.log("📤 [DELETE GROUP] Group ID:", groupId, "| Endpoint:", endpoint)
  const response = await professorRequest.sendRequest({
    endpoint,
    body: null,
    method: "patch",
  })
  logger.log("📥 [DELETE GROUP] Response:", response)
  if (!response.success) {
    logger.error("[groups] Failed to delete group:", response.message)
  }
  return response
}

export const inviteCoTeacher = async (username, groupIds) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/coteachers`,
    body: { username, groupIds },
    method: "post",
  })
  logger.log("📤 [INVITE COTEACHER] Payload:", { username, groupIds })
  logger.log("📥 [INVITE COTEACHER] Response:", response)
  if (!response.success) {
    logger.error("[groups] Failed to invite co-teacher:", response.message)
  }
  return response
}

export const getGroupCoTeachers = async (groupId) => {
  const endpoint = `${prefix}/${groupId}/coteachers`
  logger.log("📤 [GET COTEACHERS] Group ID:", groupId, "| Endpoint:", endpoint)
  const response = await professorRequest.sendRequest({
    endpoint,
    body: null,
    method: "get",
  })
  logger.log("📥 [GET COTEACHERS] Response:", response)
  if (!response.success) {
    logger.error("[groups] Failed to get co-teachers:", response.message)
  }
  return response
}

export const removeGroupCoTeachers = async (groupId, userIds) => {
  const endpoint = `${prefix}/coteachers`
  const payload = { groupId, userIds }
  logger.log(
    "📤 [REMOVE COTEACHERS] Payload:",
    JSON.stringify(payload, null, 2)
  )
  const response = await professorRequest.sendRequest({
    endpoint,
    body: payload,
    method: "delete",
  })
  logger.log("📥 [REMOVE COTEACHERS] Response:", response)
  if (!response.success) {
    logger.error("[groups] Failed to remove co-teachers:", response.message)
  }
  return response
}

export const archiveGroup = async ({ groupId, isArchived = true }) => {
  const endpoint = `${prefix}/${groupId}/archived?isArchived=${isArchived}`
  logger.log("📤 [ARCHIVE GROUP] Group ID:", groupId, "| Endpoint:", endpoint)
  const response = await professorRequest.sendRequest({
    endpoint,
    body: null,
    method: "patch",
  })
  logger.log("📥 [ARCHIVE GROUP] Response:", response)
  if (!response.success) {
    logger.error("[groups] Failed to archive group:", response.message)
  }
  return response
}

export const duplicateGroup = async (
  groupId,
  { name, copyContent, copyStudents }
) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/${groupId}/copy`,
    body: {
      name,
      copyContent,
      copyStudents,
    },
    method: "post",
  })
  if (!response.success) {
    logger.error("[groups] Failed to duplicate group:", response.message)
  }
  return response
}

export const getCreateLicenses = async ({
  learningLanguageId = 0,
  groupId = 0,
} = {}) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/content/licenses?learningLanguageId=${learningLanguageId}&groupId=${groupId}`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error("[groups] Failed to fetch licenses:", response.message)
    return null
  }
  return response.data?.data || response.data || null
}

export const checkHasActiveGroup = async () => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/hasActive`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error(
      "[groups] Failed to check for active groups:",
      response.message
    )
    return false
  }
  return response.data?.data?.hasActiveGroup
}

export const getStudentsAndSegmentsByGroupId = async ({ groupId }) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/${groupId}/students`,
    body: null,
    method: "get",
  })
  const emptyResult = { students: [], segments: [] }
  if (!response.success) {
    logger.error("[groups] Failed to get students by group:", response.message)
    return emptyResult
  }
  return response.data?.data || emptyResult
}

export const setSegmentationSetupCompleted = async ({
  groupId,
  isCompleted = true,
}) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/${groupId}/segmentationSetupCompleted`,
    body: { isCompleted },
    method: "put",
  })
  if (!response.success) {
    logger.error(
      "[groups] Failed to set segmentation setup completed:",
      response.message
    )
  }
  return response
}

export const getSegmentationSetupCompleted = async ({ groupId }) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/${groupId}/segmentationSetupCompleted`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error(
      "[groups] Failed to get segmentation setup completed:",
      response.message
    )
  }
  return response?.data?.data
}
/**
 * Get placement status for students in a group and its segments
 * @param {string} groupId - The ID of the group to get the placement status for
 * @param {string} status - The group status to filter by (optional), the status can be "All", "Active", "Archived", "Deleted"
 * @returns {Promise<object>} - The placement status for students in the group
 */
export const getPlacementStatusForStudents = async ({
  groupId,
  status = "Active",
}) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/${groupId}/students/placementStatus?status=${status}`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error(
      "[groups] Failed to get placement status for students:",
      response.message
    )
  }
  return response?.data?.data
}

export const getStudentGroupSegmentInfo = async (studentUserId) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/students/${studentUserId}/info`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error(
      `[groups] Failed to get student info for ${studentUserId}:`,
      response.message
    )
  }
  return response?.data?.data
}

export const getGroupLateWorkPenalties = async (groupId) => {
  const response = await professorRequest.sendRequest({
    endpoint: `${prefix}/${groupId}/lateWorkPenalties`,
    body: null,
    method: "get",
  })
  if (!response.success) {
    logger.error(
      `[groups] Failed to get late work penalties for group ${groupId}:`,
      response.message
    )
    return {
      allowWorkAcceptedLate: false,
      isWorkAcceptedLate: false,
      workAcceptedLate: [],
    }
  }
  return (
    response.data?.data || {
      allowWorkAcceptedLate: false,
      isWorkAcceptedLate: false,
      workAcceptedLate: [],
    }
  )
}
